import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Grid,
  Card,
  CardContent,
  CardMedia,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  DragIndicator as DragIcon,
  AccessTime as TimeIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon,
  Widgets as WidgetIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Close as CloseIcon,
  PlaylistAddOutlined
} from '@mui/icons-material';
import {Layers} from 'lucide-react';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { format } from 'date-fns';
import { sumDurationsMs } from '../formatMs';

const PlaylistItemsPanel = ({ 
  selectedPlaylist, 
  onUpdateDuration, 
  onRemoveItem, 
  onSortItems,
  onScheduleItem,
  onAddItem,
  onClose,
  socketRef,
  readOnly = false,
}) => {
  const [durationDialog, setDurationDialog] = useState({ open: false, item: null });
  const [duration, setDuration] = useState({ minutes: 0, seconds: 0 });
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, item: null });
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);

  const handleDurationClick = (item) => {
    const totalSeconds = Math.floor(item.duration / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    setDuration({ minutes, seconds });
    setDurationDialog({ open: true, item });
  };

  const handleDurationSave = () => {
    if (durationDialog.item) {
      const totalSeconds = (duration.minutes * 60) + duration.seconds;
      const totalMilliseconds = totalSeconds * 1000;
      
      onUpdateDuration(durationDialog.item, totalMilliseconds);
      setDurationDialog({ open: false, item: null });
    }
  };

  const handleRemoveItem = (item) => {
    if (window.confirm(`Are you sure you want to remove "${item.title || item.name}" from this playlist?`)) {
      onRemoveItem(item);
    }
  };

  const handleScheduleClick = (item) => {
    setScheduleDialog({ open: true, item });
  };

  const handleScheduleSave = () => {
    // This would typically open a schedule dialog
    // For now, we'll just close the dialog
    setScheduleDialog({ open: false, item: null });
  };

  const handleDragStart = (e, item, index) => {
    console.log('Drag started:', item, index);
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(index);
  };

  const handleDragLeave = () => {
    setDraggedOver(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    console.log('Drop at index:', dropIndex);
    console.log('Dragged item:', draggedItem);
    
    if (!draggedItem || draggedItem.index === dropIndex) {
      setDraggedItem(null);
      setDraggedOver(null);
      return;
    }

    const items = Array.from(selectedPlaylist.content);
    const [reorderedItem] = items.splice(draggedItem.index, 1);
    items.splice(dropIndex, 0, reorderedItem);
    
    console.log('Reordered items:', items);
    console.log('Calling onSortItems with:', items);

    onSortItems(items);
    setDraggedItem(null);
    setDraggedOver(null);
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'image':
        return <ImageIcon />;
      case 'video':
        return <VideoIcon />;
      case 'pdf':
        return <PdfIcon />;
      case 'widget':
        return <WidgetIcon />;
        case 'template':
         return <Layers/>
      default:
        return <ImageIcon />;
    }
  };

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'image':
        return '#4caf50';
      case 'video':
        return '#f44336';
      case 'pdf':
        return '#ff9800';
      case 'widget':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTotalDuration = () => {
    if (!selectedPlaylist?.content) return '0:00';
    const totalMs = sumDurationsMs(selectedPlaylist.content);
    return formatDuration(totalMs);
  };

  if (!selectedPlaylist) {
    return (
      <Paper sx={{ height: '100%', p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select a Playlist
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a playlist from the list to view and manage its content
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: '100%', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#008080', width: 48, height: 48 }}>
              {selectedPlaylist.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="medium">
                {selectedPlaylist.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlaylist.content?.length || 0} items • Total: {getTotalDuration()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!readOnly && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PlaylistAddOutlined />}
              onClick={() => onAddItem && onAddItem(selectedPlaylist)}
              sx={{ 
                backgroundColor: '#008080',
                '&:hover': { backgroundColor: '#006666' }
              }}
            >
              Add Item
            </Button>
            )}
            {onClose && (
              <IconButton onClick={onClose} size="small" color="primary">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        
        {selectedPlaylist.schedule?.scheduled && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Scheduled: {format(new Date(selectedPlaylist.schedule.startDate), 'MMM dd, yyyy')} - {format(new Date(selectedPlaylist.schedule.endDate), 'MMM dd, yyyy')}
            </Typography>
            <Typography variant="caption">
              {selectedPlaylist.schedule.startTime} - {selectedPlaylist.schedule.endTime}
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Content List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!selectedPlaylist.content || selectedPlaylist.content.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No content in this playlist
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add content to get started
            </Typography>
          </Box>
        ) : (
          <List>
            {selectedPlaylist.content.map((item, index) => (
              <ListItem
                key={item._id}
                draggable={!readOnly}
                onDragStart={readOnly ? undefined : (e) => handleDragStart(e, item, index)}
                onDragOver={readOnly ? undefined : (e) => handleDragOver(e, index)}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : (e) => handleDrop(e, index)}
                sx={{
                  border: 1,
                  borderColor: draggedItem?.index === index ? '#008080' : 'divider',
                  borderRadius: 1,
                  mb: 1,
                  mx: 1,
                  backgroundColor: draggedItem?.index === index ? '#e3f2fd' : 
                                 draggedOver === index ? '#f0f8ff' : 'white',
                  boxShadow: draggedItem?.index === index ? '0 4px 8px rgba(0,0,0,0.2)' : 'none',
                  transform: draggedItem?.index === index ? 'rotate(2deg)' : 'none',
                  transition: 'all 0.2s ease',
                  cursor: readOnly ? 'default' : 'grab',
                  '&:hover': {
                    backgroundColor: '#f9f9f9'
                  }
                }}
              >
                {!readOnly && (
                <Tooltip title="Drag to reorder">
                  <Box sx={{ mr: 1, cursor: 'grab', display: 'flex', alignItems: 'center', p: 0.5 }}>
                    <DragIcon color="action" />
                  </Box>
                </Tooltip>
                )}

                {/* Content Preview */}
                <Box sx={{ mr: 2 }}>
                  {item.url && (item.type === 'image' || item.type === 'video') ? (
                    <Box sx={{ position: 'relative' }}>
                      {
                        item.type=='image' &&
                      <CardMedia
                        component="img"
                        sx={{ width: 60, height: 40, borderRadius: 1 }}
                        image={item.url}
                        alt={item.title || item.name}
                        loading="lazy"
                      />
                      }
                       {
                        item.type=='video' &&
                      <CardMedia
                        component="video"
                        sx={{ width: 60, height: 40, borderRadius: 1 }}
                        image={item.url}
                        alt={item.title || item.name}
                        loading="lazy"
                      />
                      }
                       {
                        item.type=='template' &&
                      <div
                        
                        style={{ width: 60, height: 40, borderRadius: 1 }}
                        
                      >
                        <Layers/>
                      </div>
                      }
                      {item.type === 'video' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <PlayIcon sx={{ color: 'white', fontSize: 12 }} />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Avatar sx={{ 
                      width: 60, 
                      height: 40, 
                      bgcolor: getContentTypeColor(item.type),
                      borderRadius: 1
                    }}>
                      {getContentIcon(item.type)}
                    </Avatar>
                  )}
                </Box>

                {/* Content Info */}
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" fontWeight="medium">
                      {item.title || item.name.substr(0,30) || 'Untitled'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={item.type || 'unknown'}
                        size="small"
                        sx={{ 
                          bgcolor: getContentTypeColor(item.type),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(item.duration || 0)}
                      </Typography>
                    </Box>
                  }
                />

                {!readOnly && (
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit Duration">
                      <IconButton
                        size="small"
                        onClick={() => handleDurationClick(item)}
                        color="primary"
                      >
                        <TimeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from Playlist">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(item)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Duration Edit Dialog */}
      <Dialog
      maxWidth='sm'
      fullWidth
      open={durationDialog.open} onClose={() => setDurationDialog({ open: false, item: null })}>
        <DialogTitle>Edit Duration</DialogTitle>
        <DialogContent sx={{width:'29w',overflow:'hidden'}}>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Content: {durationDialog.item?.title || durationDialog.item?.name || 'Untitled'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  label="Minutes"
                  type="number"
                  value={duration.minutes}
                  onChange={(e) => setDuration({ ...duration, minutes: parseInt(e.target.value) || 0 })}
                  fullWidth
                  inputProps={{ min: 0, max: 59 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Seconds"
                  type="number"
                  value={duration.seconds}
                  onChange={(e) => setDuration({ ...duration, seconds: parseInt(e.target.value) || 0 })}
                  fullWidth
                  inputProps={{ min: 0, max: 59 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDurationDialog({ open: false, item: null })}>
            Cancel
          </Button>
          <Button onClick={handleDurationSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog.open} onClose={() => setScheduleDialog({ open: false, item: null })}>
        <DialogTitle>Schedule Content</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Content: {scheduleDialog.item?.title || scheduleDialog.item?.name || 'Untitled'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Schedule functionality will be implemented here.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog({ open: false, item: null })}>
            Cancel
          </Button>
          <Button onClick={handleScheduleSave} variant="contained">
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PlaylistItemsPanel;
